import { ScrollControlTypes } from "./scroll-control";

const isNegativeRTL = () => {
  const ua = navigator.userAgent.toLowerCase();
  // @ts-expect-error documentMode does only exist in IE so I don't see any alternative to ts-ignore
  const isIE = !!document.documentMode;
  const isFirefox = ua.indexOf("gecko") > -1;
  const isSafari = ua.indexOf("safari") > -1;
  const isChrome = ua.indexOf("chrome") > -1;

  return (isSafari || isFirefox) && !isChrome && !isIE;
};

// In IE sometimes because of inconvenient rounding we get wrong proportions
// between container and content widths, so scrolling becomes allowed when it shouldn't
// 1px buffer prevents it (though sometimes hides 1px from the real button,
// but it's not really visible.
const getContainerWidthForIE = (width: number) => width + 1;

const isScrollingAllowedCases = {
  ltr: {
    all: ({ scrollLeft, scrollWidth, clientWidth }: Element) => ({
      start: scrollLeft > 0,
      end: Math.ceil(scrollLeft) < scrollWidth - clientWidth,
    }),

    ie: ({ scrollLeft, scrollWidth, clientWidth }: Element) => ({
      start: scrollLeft > 0,
      end:
        Math.ceil(scrollLeft) <
        scrollWidth - getContainerWidthForIE(clientWidth),
    }),
  },

  rtl: {
    chrome: ({ scrollLeft, scrollWidth, clientWidth }: Element) => ({
      start: Math.ceil(scrollLeft) < scrollWidth - clientWidth,
      end: scrollLeft > 0,
    }),
    negativeRTL: ({ scrollLeft, scrollWidth, clientWidth }: Element) => ({
      start: scrollLeft < 0,
      end: Math.ceil(Math.abs(scrollLeft)) < scrollWidth - clientWidth,
    }),
    ie: ({ scrollLeft, scrollWidth, clientWidth }: Element) => ({
      start: scrollLeft > 0,
      end:
        Math.ceil(scrollLeft) <
        scrollWidth - getContainerWidthForIE(clientWidth),
    }),
  },
};

export const getIsScrollingAllowed = ({
  element,
  isRTL,
}: {
  element: Element;
  isRTL?: boolean;
}) => {
  let isScrollingAllowed;

  if (isRTL) {
    if (isNegativeRTL()) {
      // Firefox and safari
      isScrollingAllowed = isScrollingAllowedCases.rtl.negativeRTL;
    } else if (!element.scrollTo) {
      isScrollingAllowed = isScrollingAllowedCases.rtl.ie;
    } else {
      isScrollingAllowed = isScrollingAllowedCases.rtl.chrome;
    }
  } else {
    if (!element.scrollTo) {
      isScrollingAllowed = isScrollingAllowedCases.ltr.ie;
    } else {
      isScrollingAllowed = isScrollingAllowedCases.ltr.all;
    }
  }
  return isScrollingAllowed(element);
};

export const getNextScrollingPosition = ({
  element,
  isRTL,
  type,
}: {
  element: Element;
  isRTL?: boolean;
  type: ScrollControlTypes;
}) => {
  const { start, end } = ScrollControlTypes;
  const {
    scrollWidth,
    scrollLeft: currentScroll,
    clientWidth: tabListWidth,
  } = element;
  const maxScroll = scrollWidth - tabListWidth;
  let direction = type === end ? 1 : -1;

  // In all browsers but IE horizontal scrolling is not inverted in RTL,
  // so initial value is equal to container's width
  // In IE it's 0, so we have to scroll in opposite direction
  if (isRTL && element.scrollTo) {
    direction = -1 * direction;
  }

  let nextScroll = currentScroll + tabListWidth * direction;

  if (isRTL && isNegativeRTL()) {
    // Safari and Firefox in RTL mode treat scrollLeft axis negatively,
    // so this entire IF branch is a workaround for this.
    // It is possible to combine calculations inside it with general ones,
    // but I believe that the nested IF, even if ugly, is more readable than
    // any sophisticated formula would be.
    if (type === start && nextScroll > 0) {
      nextScroll = 0;
    } else if (nextScroll < -maxScroll) {
      nextScroll = -maxScroll;
    }
  } else if (nextScroll > maxScroll) {
    nextScroll = maxScroll;
  } else if (nextScroll < 0) {
    nextScroll = 0;
  }

  return nextScroll;
};
